/**
 * The 10-slot view of the player's own system (galaxy plan Milestone B/C) —
 * shown as a second tab inside SectorView rather than its own nav entry,
 * to avoid adding an 11th item to the already-full mobile dock.
 *
 * NPC slots can be attacked directly (exact fleet picker, same guards as
 * CampsView); empty slots can be colonized directly if the player meets
 * the usual colonization requirements. Attacking/colonizing another real
 * player's slot is supported by the PvP endpoint; the first version keeps
 * target selection inside the currently visible system.
 */

import { useEffect, useState } from 'react';
import { Button, Countdown, FleetPicker, Panel, SystemGrid, type GalaxySlot } from '@spacegame/ui';
import { COLONY_MIN_LEVEL, COMBAT_CAPABLE, MAX_PLANETS, UNITS, computeModifiers, estimateFuelCost, fleetCargo, fleetTravelTime, type ShipKey } from '@spacegame/engine';
import { fleetCapacityStatus } from '../game/selectors';
import { useGame, useGameActions } from '../game/GameProvider';
import { useAuth } from '../game/auth-context';
import { GalaxyClient } from '../game/galaxy-client';

export function GalaxyView({ target }: { target?: { ring: 1 | 2 | 3 | 4 | 5; system: number; position: number } | null }) {
  const { game, clock } = useGame();
  const actions = useGameActions();
  const auth = useAuth();
  const ring = target?.ring ?? game.planets[0]!.ring;
  const system = target?.system ?? game.planets[0]!.system;
  const [slots, setSlots] = useState<GalaxySlot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fleet, setFleet] = useState<Partial<Record<ShipKey, number>>>({});
  const [attackMessage, setAttackMessage] = useState<string | null>(null);
  const [scoutMessage, setScoutMessage] = useState<string | null>(null);
  const [scoutProbes, setScoutProbes] = useState(1);
  const now = clock();
  const fleetCapacity = fleetCapacityStatus(game);
  const combatShips = COMBAT_CAPABLE.filter((key) => game.planets[0]!.ships[key as ShipKey] > 0) as ShipKey[];
  const fleetSize = Object.values(fleet).reduce((sum, count) => sum + (count ?? 0), 0);
  const fleetRows = combatShips.map((key) => ({ id: key, label: UNITS[key].label, available: game.planets[0]!.ships[key], value: fleet[key] ?? 0 }));
  const fleetFuel = estimateFuelCost(game, fleet);
  const fleetTime = fleetSize > 0 ? fleetTravelTime(60_000, fleet, computeModifiers(game, now).flightSpeed) : 0;

  useEffect(() => {
    let cancelled = false;
    setSlots(null);
    setError(null);
    new GalaxyClient(auth)
      .getSystem(ring, system)
      .then((result) => {
        if (!cancelled) setSlots(result);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Unbekannter Fehler.');
      });
    return () => {
      cancelled = true;
    };
  }, [auth, ring, system]);

  if (error) {
    return <p className="p-4 text-center text-xs leading-relaxed text-danger-bright">{error}</p>;
  }

  if (!slots) {
    return <p className="p-4 text-center text-xs text-content-muted">Lade Systemdaten …</p>;
  }

  const attack = async (slot: GalaxySlot) => {
    setAttackMessage(null);
    const result = await actions.attackNpcColony(ring, system, slot.position, fleet);
    setFleet({});
    if (result) setAttackMessage(result.events[0]?.message ?? 'Der Angriff wurde ausgewertet.');
    setFleet({});
  };

  const attackPlayer = async (slot: GalaxySlot) => {
    setAttackMessage(null);
    const result = await actions.attackPlayer(ring, system, slot.position, fleet);
    setFleet({});
    if (result) setAttackMessage(result.events[0]?.message ?? 'Die PvP-Flotte wurde entsendet.');
    setFleet({});
  };

  const scout = async (slot: GalaxySlot) => {
    setScoutMessage(null);
    const probes = scoutProbes;
    const result = await actions.scoutNpcColony(ring, system, slot.position, probes);
    if (!result) return;
    const report = result.events.find((event) => event.type === 'scoutReport');
    if (report?.data) {
      setScoutMessage(`${report.message} Garnison: ${JSON.stringify(report.data.garrison)} · Beute: ${JSON.stringify(report.data.loot)}`);
    } else {
      setScoutMessage(result.events.find((event) => event.type === 'scoutLost')?.message ?? 'Die Spähaktion wurde ausgewertet.');
    }
  };

  const scoutPlayer = async (slot: GalaxySlot) => {
    setScoutMessage(null);
    const result = await actions.scoutPlayer(ring, system, slot.position, scoutProbes);
    if (!result) return;
    setScoutMessage(result.events[0]?.message ?? 'Die Spieler-Aufklärung wurde ausgewertet.');
  };

  const scoutMissionFor = (slot: GalaxySlot) => game.fleetMissions.find(
    (mission): mission is Extract<typeof mission, { kind: 'scoutPlayer' }> => mission.kind === 'scoutPlayer'
      && mission.target.ring === ring
      && mission.target.system === system
      && mission.target.position === slot.position,
  );

  return (
    <div className="space-y-3 p-1">
      <p className="text-xs leading-relaxed text-content-muted">
        Ring {ring} · System {system} — deine Kolonie und ihre Nachbarn.
      </p>
      <label className="flex items-center gap-2 text-xs text-content-secondary">Aero-Sonden
        <input className="h-7 w-14 rounded-control border border-edge bg-surface-3 text-center" type="number" min={1} max={game.planets[0]!.ships.probe} value={scoutProbes} onChange={(event) => setScoutProbes(Math.min(game.planets[0]!.ships.probe, Math.max(1, Number(event.target.value) || 1)))} />
      </label>
      <Panel title="Angriffsflotte" eyebrow={`${fleetSize} Kampfschiffe ausgewählt`}>
        <FleetPicker rows={fleetRows} onChange={(id, value) => setFleet((current) => ({ ...current, [id]: value }))} totalShips={fleetSize} cargo={fleetCargo(game, fleet)} fuel={fleetFuel} fuelAvailable={game.resources.isotope} travelTimeMs={fleetTime} />
        <p className="mt-2 text-xs text-content-muted">Flotten: {fleetCapacity.active}/{fleetCapacity.capacity}</p>
      </Panel>
      {attackMessage ? <p className="rounded-control border border-accent/40 bg-accent/10 p-3 text-sm text-content">{attackMessage}</p> : null}
      {scoutMessage ? <p className="rounded-control border border-aether/40 bg-aether/10 p-3 text-sm text-content">{scoutMessage}</p> : null}
      <SystemGrid
        slots={slots}
        renderAction={(slot) => {
          if (slot.occupantKind === 'npc') {
            return (
              <div className="flex w-full flex-col gap-1">
                <Button variant="secondary" size="sm" fullWidth disabled={game.planets[0]!.ships.probe < 1} onClick={() => void scout(slot)}>
                  Spähen
                </Button>
                <Button variant="danger" size="sm" fullWidth disabled={fleetSize === 0 || fleetCapacity.full} onClick={() => void attack(slot)}>
                  Angreifen
                </Button>
              </div>
            );
          }
          if (slot.occupantKind === 'player' && !slot.isMine) {
            const mission = scoutMissionFor(slot);
            return (
              <div className="flex w-full flex-col gap-1">
                <Button variant="secondary" size="sm" fullWidth disabled={game.planets[0]!.ships.probe < 1 || Boolean(mission) || fleetCapacity.full} onClick={() => void scoutPlayer(slot)}>
                  {mission ? <>Sonden unterwegs <Countdown target={mission.arrivesAt} format="short" /></> : 'Spähen'}
                </Button>
                <Button variant="danger" size="sm" fullWidth disabled={fleetSize === 0 || fleetCapacity.full} onClick={() => void attackPlayer(slot)}>
                  PvP-Angriff
                </Button>
              </div>
            );
          }
          if (
            slot.occupantKind === 'empty' &&
            game.planets.length < MAX_PLANETS &&
            game.level >= COLONY_MIN_LEVEL &&
            game.planets[0]!.ships.colonyShip >= 1
          ) {
            return (
              <Button
                size="sm"
                variant="secondary"
                onClick={async () => {
                  const result = await actions.colonizeAt(system, slot.position);
                  if (result) {
                    setSlots((current) => current?.map((entry) =>
                      entry.position === slot.position
                        ? { ...entry, occupantKind: 'player', isMine: true }
                        : entry,
                    ) ?? null);
                  }
                }}
              >
                Hier kolonisieren
              </Button>
            );
          }
          return undefined;
        }}
      />
    </div>
  );
}
